import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useUpdateServiceAccount, type PlainServiceAccount } from "@/queries/service-account-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC, type FormEvent } from "react";
import { ServiceAccountFormFields } from "@/components/settings/organization/service-accounts/ServiceAccountFormFields";

type EditServiceAccountDialogProps = {
    open: boolean;
    onClose: () => void;
    serviceAccount: PlainServiceAccount;
};

export const EditServiceAccountDialog: FC<EditServiceAccountDialogProps> = ({ open, onClose, serviceAccount }) => {
    const { toast } = useToast();
    const updateServiceAccount = useUpdateServiceAccount();
    const [name, setName] = useState(serviceAccount.name);
    const [description, setDescription] = useState(serviceAccount.description || "");

    // Reset form when service account changes or dialog opens
    useEffect(() => {
        if (open) {
            setName(serviceAccount.name);
            setDescription(serviceAccount.description || "");
        }
    }, [open, serviceAccount.name, serviceAccount.description]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!name.trim()) {
                return;
            }

            try {
                await updateServiceAccount.mutateAsync({
                    serviceAccountId: serviceAccount.id,
                    name: name.trim(),
                    description: description.trim() || undefined,
                });
                onClose();
            } catch (error) {
                toast({
                    title: "Failed to update service account",
                    description: formatError(error),
                });
            }
        },
        [name, description, serviceAccount.id, updateServiceAccount, toast, onClose],
    );

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    const isFormValid = name.trim().length >= 1 && name.trim().length <= 64;
    const hasChanges = name !== serviceAccount.name || description !== (serviceAccount.description || "");

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="edit-service-account-dialog"
                data-track-location={TrackLocations.ServiceAccountEditDialog}
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle>Edit Service Account</DialogTitle>
                </DialogHeader>

                <DialogBody>
                    <form id="edit-service-account-form" onSubmit={handleSubmit}>
                        <ServiceAccountFormFields
                            name={name}
                            description={description}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            autoFocus
                        />
                    </form>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="edit-service-account-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="edit-service-account-form"
                        variant="primary"
                        disabled={!isFormValid || !hasChanges || updateServiceAccount.isPending}
                        loading={updateServiceAccount.isPending}
                        data-testid="edit-service-account-dialog-save"
                        data-tracking-id="edit-service-account-dialog-save"
                    >
                        Update
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
